import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import { GspNumberType } from 'src/app/shared/common/components/gsp-number/gsp-number.component';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { Style } from 'src/app/shared/map-data-services/styles/style';
import { StyleSymbologyStreamService } from 'src/app/shared/map-data-services/styles/style-symbology-stream.service';
import {
    ChoiceField,
    ChoiceValue,
    CodedChoiceValue,
    Field,
    FieldType,
    LayoutFieldType
} from 'src/app/shared/template-services/field';
import { Template } from 'src/app/shared/template-services/template';
import {
    AttributeSymbologyOptions,
    LayerSymbologyType,
    LayerSymbolTypes,
    LineStyleOptions,
    SymbologyOverrideChoice,
    SymbolShapeOptions
} from '../../../shared/map-data-services/styles/style-symbology';
import { Domains } from '../template/domains';
import { TemplateStoreForComponent } from '../template/template-store-for-component.service';

@Component({
    selector: 'style-symbology-panel',
    templateUrl: './style-symbology-panel.component.html'
})
export class SymbologyPanelComponent implements OnInit, OnDestroy {
    @Input()
    template: Template;

    @Input()
    templateStyle: Style;

    @Input()
    // This is true when the component is rendered inside the Layer Style edit panel.
    layerEditMode: boolean = false;

    @Input()
    layer?: Layer;

    private previousTemplate: Template;
    public loading = false;

    public layerSymbolTypes: LayerSymbolTypes[];
    public layerSymbolShapes: SymbolShapeOptions[];

    // Line and area templates
    readonly lineStyleOptions: LineStyleOptions[] = Domains.getLineStyleOptions();

    // Attribute-based symbology
    public templateChoiceFields: Field[];
    public templateChoiceFieldOptions: AttributeSymbologyOptions[] = [];
    public attributeChoiceFieldId: string;
    public selectedChoiceOverrideOptions: AttributeSymbologyOptions[] = [];
    public selectedChoiceOverrideId: string;
    private selectedChoiceOverrideOptionsCopy: AttributeSymbologyOptions[] = [];
    public styleOverrideEmptyMessage: string;

    // Expose enum to template
    public LayerSymbologyType = LayerSymbologyType;
    public GeometryTypes = GeometryTypes;
    public GspNumberType = GspNumberType;
    public ButtonType = ButtonType;
    public NotificationType = NotificationType;

    private destroyed$ = new Subject<void>();

    constructor(
        private templateStoreForComponent: TemplateStoreForComponent,
        private styleSymbologyStreamService: StyleSymbologyStreamService
    ) {}

    ngOnInit() {
        this.initializeSymbols();
        this.setDefaultColors();
        this.setTemplateChoiceFields();

        this.checkForTemplateChanges();
    }

    private initializeSymbols() {
        this.layerSymbolTypes = Domains.getLayerSymbolTypes();

        if (this.template.geometryType !== GeometryTypes.POINT) {
            // Line, Area features should not have feature symbols.
            this.layerSymbolTypes = this.layerSymbolTypes.filter(type => type.name !== LayerSymbologyType.FEATURE);
        }

        this.template.enableSymbols = this.template.geometryType === GeometryTypes.POINT;

        if (this.template.enableSymbols) {
            this.symbolTypeSelected(this.templateStyle.symbol.type, true);
        } else {
            this.setDefaultColors();
        }
    }

    public symbolTypeSelected(symbolType: LayerSymbologyType, initialize = false) {
        // Initialize the symbol shapes based on the selected symbol type.
        this.loading = true;

        this.templateStyle.symbol.type = symbolType;
        this.layerSymbolShapes = this.getSymbolShapeOptions(symbolType);

        let defaultSymbol = this.layerSymbolShapes[0];

        if (this.templateStyle.symbol.type === LayerSymbologyType.GEOMETRIC) {
            // Default Point symbol is Geometric - Dot.
            const defaultDotSymbol = this.styleSymbologyStreamService.symbolDefinitionsStream
                .getValue()
                .find(symbol => symbol.uuid === this.styleSymbologyStreamService.DEFAULT_SYMBOL_UUID);
            defaultSymbol =
                this.layerSymbolShapes.find(symbol => symbol.id === defaultDotSymbol?.uuid) || defaultSymbol;
        }

        const defaultSymbolShape = this.layerSymbolShapes.find(symbol => symbol.id === defaultSymbol?.id);

        if (initialize) {
            this.templateStyle.symbol.symbolName =
                this.templateStyle.symbol.symbolName &&
                this.layerSymbolShapes.find(symbol => symbol.id === this.templateStyle.symbol.symbolDefinitionUuid)
                    ?.name
                    ? this.templateStyle.symbol.symbolName
                    : defaultSymbolShape?.name;
        } else {
            this.templateStyle.symbol.symbolDefinitionUuid = defaultSymbolShape?.id;
            this.templateStyle.symbol.symbolName = defaultSymbolShape?.name;
        }

        this.symbolShapeSelected(this.templateStyle.symbol.symbolName);
        this.setDefaultColors();

        setTimeout(() => {
            this.loading = false;
        }, 0);
    }

    private setDefaultColors() {
        let defaultColor: number;

        if (!this.template.id) {
            if (this.layerEditMode && this.layer?.color) {
                // For imported layers, set default color based on layer color - for Edit panel.
                defaultColor = LayerStyleGenerator.hexToDecimalRgb(this.layer.color);

                if (this.templateStyle.symbol.id) {
                    // Imported layer, but has associated Style. Do not set colors to default - for Layer Edit.
                    return;
                }
            } else if (this.template.attachedToLayers[0]?.style?.id) {
                // Imported layer, but has associated Style. Set default color based on Existing Style - for Template Wiz.
                const layerStyle = this.template.attachedToLayers[0].style;
                if (this.template.geometryType === GeometryTypes.POINT) {
                    this.templateStyle.symbol = layerStyle.symbol;
                } else {
                    this.templateStyle.fillColor = layerStyle.fillColor;
                    this.templateStyle.lineColor = layerStyle.lineColor;
                }
                this.layerSymbolShapes = this.getSymbolShapeOptions(this.templateStyle.symbol.type);

                return;
            } else {
                // For new templates, set default color to red if not defined.
                // geometryColorHexRGB is already set if template is being created from an imported layer.
                defaultColor = LayerStyleGenerator.hexToDecimalRgb(this.template.geometryColorHexRGB) || 0xda212c;
            }

            if (this.templateStyle.symbol.type === LayerSymbologyType.CUSTOMIZABLE) {
                // White fill for Customizable Icons.
                this.templateStyle.symbol.fillColor = 0xffffff;

                this.templateStyle.symbol.outlineColor = defaultColor;
                this.templateStyle.symbol.color = defaultColor;
            } else {
                // Default for other Icons.
                this.templateStyle.symbol.fillColor = defaultColor;
                this.templateStyle.symbol.outlineColor = defaultColor;
                this.templateStyle.fillColor = defaultColor;
                this.templateStyle.lineColor = defaultColor;
            }
        } else if (this.template.geometryType === GeometryTypes.POINT && !this.templateStyle.symbol.id) {
            // Older point templates created without Symbol.
            //Color of the layer style used here, because as we are not updating the template while changing the style of the layer in prod, so it doesn't have updated style color.
            if (!this.layerEditMode) defaultColor = this.templateStyle.fillColor;
            else defaultColor = LayerStyleGenerator.hexToDecimalRgb(this.template.geometryColorHexRGB);

            this.templateStyle.symbol.fillColor = defaultColor;
            this.templateStyle.symbol.outlineColor = defaultColor;
            this.templateStyle.symbol.color = defaultColor;
        }
    }

    private getSymbolShapeOptions(symbolType: LayerSymbologyType): SymbolShapeOptions[] {
        const symbolDefinitions = this.styleSymbologyStreamService.symbolDefinitionsStream.getValue();
        const symbolDefintionsForType = symbolDefinitions.filter(symbol => symbol.type === symbolType);
        return symbolDefintionsForType.map(symbol => ({
            id: symbol.uuid,
            name: symbol.symbolName,
            label: symbol.symbolName,
            iconClass: symbol.imageClass
        }));
    }

    public symbolShapeSelected(symbolShapeName: string) {
        this.templateStyle.symbol.symbolDefinitionUuid = this.layerSymbolShapes.find(
            shape => shape.name === symbolShapeName
        ).id;
    }

    private setTemplateChoiceFields() {
        const styleOverrides = this.templateStyle.styleOverrides as SymbologyOverrideChoice[];

        this.templateStoreForComponent.templateEditorFields.pipe(takeUntil(this.destroyed$)).subscribe(fields => {
            this.updateTemplateChoiceFields(fields);
        });
        this.updateTemplateChoiceFields(this.template.fields);

        if (styleOverrides?.length) {
            this.template.enableAttributedSymbols = true;

            this.attributeChoiceFieldId = this.templateChoiceFields.find(
                choice => choice.name === styleOverrides[0].choiceField
            )?.uuid;

            this.choiceFieldSelected(this.attributeChoiceFieldId, false);

            // Remove the selected choice overrides from the options
            this.selectedChoiceOverrideOptions = this.selectedChoiceOverrideOptions.filter(
                item => !styleOverrides.some(override => override.choiceValue === item.name)
            );
        } else {
            this.template.enableAttributedSymbols = false;
        }

        this.styleOverrideEmptyMessage = this.layerEditMode
            ? 'TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.LayerStyleOverridesEmpty'
            : 'TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.TemplateStyleOverridesEmpty';
    }

    private checkForTemplateChanges() {
        setInterval(() => {
            if (this.previousTemplate && !_.isEqual(this.template.geometryType, this.previousTemplate.geometryType)) {
                this.initializeSymbols();
            }
            this.previousTemplate = _.cloneDeep(this.template);
        }, 250);
    }

    private updateTemplateChoiceFields(fields: Field[]) {
        // If fields are in a Group, flatten them.
        fields = fields.flatMap(field => (field.type === LayoutFieldType.Group ? field.fields : field));

        const choiceAndCodedChoiceFields = fields.filter(
            field =>
                (field.type === FieldType.Choice && !(field as ChoiceField).allowMultiSelect) ||
                field.type === FieldType.CodedChoice
        );

        this.templateChoiceFields = choiceAndCodedChoiceFields;

        this.templateChoiceFieldOptions = this.templateChoiceFields.map(field => ({
            id: field.uuid,
            name: field.name,
            label: field.displayName,
            type: field.type.toLowerCase()
        }));

        if (!this.layerEditMode) {
            // Check if override attributeChoiceField has been removed from the template.
            const isAttributeChoiceFieldIdValid = this.templateChoiceFields.some(
                field => field.uuid === this.attributeChoiceFieldId
            );

            if (isAttributeChoiceFieldIdValid) {
                this.choiceFieldSelected(this.attributeChoiceFieldId, false);
            } else {
                this.attributeChoiceFieldId = null;
                this.selectedChoiceOverrideOptions = [];
                if (!this.template.id) {
                    this.templateStyle.styleOverrides = [];
                }
            }
        }
    }

    public choiceFieldSelected(fieldId: string, clearOverrides: boolean = true) {
        const selectedField = this.templateChoiceFields.find(field => field.uuid === fieldId);

        if (selectedField?.type === FieldType.Choice) {
            const choiceValues = selectedField.values as ChoiceValue[];
            this.selectedChoiceOverrideOptions = choiceValues.map(item => ({
                id: item.uuid || item.text + '-id',
                name: item.text,
                label: item.text,
                type: 'choice'
            }));
        } else if (selectedField?.type === FieldType.CodedChoice) {
            const codedChoiceValues = selectedField.values as CodedChoiceValue[];
            this.selectedChoiceOverrideOptions = codedChoiceValues.map(item => ({
                id: item.uuid || item.code + '-id',
                name: item.code,
                label: item.description,
                type: 'codedchoice'
            }));
        }

        // Create Template: If a choice value has been removed from a choice field, remove that choice if it's in the list of selected overrides.
        // Edit Template:  Do not remove, since there might exist a form with that override from a previous template version.
        // However, allow the user to remove it from the Edit Layer panel in any case. [TCMAPS-4570]
        if (!this.layerEditMode && !this.template.id) {
            // Remove override only if the choice value field is actually removed and not edited.
            this.templateStyle.styleOverrides = (
                this.templateStyle.styleOverrides as SymbologyOverrideChoice[]
            )?.filter(item => {
                const matchingChoice = this.selectedChoiceOverrideOptions.find(choice => choice.id === item.choiceUuid);
                if (matchingChoice) {
                    item.choiceValue = matchingChoice.name;
                    return true;
                }
                return false;
            });
        }

        this.selectedChoiceOverrideOptionsCopy = _.cloneDeep(this.selectedChoiceOverrideOptions);

        if (!clearOverrides) {
            // If an override is already in the selected list, remove it from the available options.
            this.selectedChoiceOverrideOptions = this.selectedChoiceOverrideOptions.filter(
                option =>
                    !(this.templateStyle.styleOverrides as SymbologyOverrideChoice[])?.some(
                        choice => choice.choiceValue === option.name
                    )
            );
        } else {
            this.templateStyle.styleOverrides = [];
        }
    }

    public validatePercentage(): void {
        if (this.templateStyle.fillOpacity > 100) {
            this.templateStyle.fillOpacity = 100;
        } else if (isNaN(this.templateStyle.fillOpacity)) {
            this.templateStyle.fillOpacity = 0;
        }
    }

    public addOverride() {
        const choiceField = this.templateChoiceFieldOptions.find(choice => choice.id === this.attributeChoiceFieldId);
        const choiceValue = this.selectedChoiceOverrideOptions.find(
            choice => choice.id === this.selectedChoiceOverrideId
        );

        const randomColor = LayerStyleGenerator.getRandomColor();

        this.templateStyle.styleOverrides = this.templateStyle.styleOverrides || [];

        (this.templateStyle.styleOverrides as SymbologyOverrideChoice[]).push({
            choiceUuid: choiceValue.id,
            choiceField: choiceField.name,
            choiceValue: choiceValue.name,
            choiceDisplayValue: choiceValue.name,
            lineColor: randomColor,
            fillColor: randomColor,
            outlineColor: randomColor,
            color: randomColor
        });

        this.selectedChoiceOverrideOptions = this.selectedChoiceOverrideOptions.filter(
            choice => choice.id !== this.selectedChoiceOverrideId
        );
        this.selectedChoiceOverrideId = this.selectedChoiceOverrideOptions[0]?.id;
    }

    public removeOverrideFromList(override: SymbologyOverrideChoice) {
        // Add the removed override back to the available options, if it is still in the template.
        const matchingChoice = this.selectedChoiceOverrideOptionsCopy.find(
            choice => choice.name === override.choiceValue
        );
        if (matchingChoice) {
            this.selectedChoiceOverrideOptions = [...this.selectedChoiceOverrideOptions, matchingChoice];
        }

        // Maintain the original order of choices.
        this.selectedChoiceOverrideOptions.sort(
            (a, b) =>
                this.selectedChoiceOverrideOptionsCopy.findIndex(item => item.name === a.name) -
                this.selectedChoiceOverrideOptionsCopy.findIndex(item => item.name === b.name)
        );

        this.templateStyle.styleOverrides = (this.templateStyle.styleOverrides as SymbologyOverrideChoice[]).filter(
            item => override.choiceValue !== item.choiceValue
        );
    }

    public getChoiceDisplayValue(override: SymbologyOverrideChoice): string {
        // Find the field that matches this override
        const selectedField = this.templateChoiceFields.find(field => field.name === override.choiceField);

        if (selectedField?.type === FieldType.CodedChoice) {
            // For coded choice fields, find the description
            const codedChoiceValues = selectedField.values as CodedChoiceValue[];
            const matchingChoice = codedChoiceValues.find(value => value.code === override.choiceValue);

            if (matchingChoice) {
                return matchingChoice.description;
            }
        }

        // For regular choice fields or if no match found, return the original value
        return override.choiceValue;
    }

    public getErrorText(): string {
        const fieldRequiredMessage = 'TC.Common.FieldRequired';
        if (!this.templateStyle.symbol.text.length) {
            return fieldRequiredMessage;
        }
    }

    public setColorForImportedLayer(color: number) {
        if (this.template.geometryType === GeometryTypes.POINT) {
            this.templateStyle.symbol.fillColor = color;
            this.templateStyle.symbol.outlineColor = color;
            this.templateStyle.symbol.color = color;
        } else if (this.template.geometryType === GeometryTypes.LINE) {
            this.templateStyle.lineColor = color;
        } else if (this.template.geometryType === GeometryTypes.AREA) {
            this.templateStyle.fillColor = color;
            this.templateStyle.lineColor = color;
        }

        this.template.geometryColorHexRGB = LayerStyleGenerator.decimalToHexRgb(color);
    }

    ngOnDestroy(): void {
        this.destroyed$.next(null);
    }
}
